/**
 * Default layout for the Setters page.
 *
 * Same structure as Closers but:
 * - Commission is 5%, emerald theme
 * - Trend chart is "Show Rate Trend" instead of "Close Rate Trend"
 * - Table is "Setter Performance"
 */

import type { DashboardLayout } from "../types";

export const SETTERS_DEFAULT: DashboardLayout = {
  widgets: [
    // --- Row 1: Metric Cards (y:0) ---
    {
      id: "setters-metric-cash",
      type: "metric",
      title: "Cash Collected",
      config: {
        metricKey: "totalCashCollected",
        format: "currency",
        icon: "dollar",
        color: "brand",
      },
    },
    {
      id: "setters-metric-revenue",
      type: "metric",
      title: "Revenue Generated",
      config: {
        metricKey: "totalRevenue",
        format: "currency",
        icon: "trending",
        color: "green",
      },
    },
    {
      id: "setters-metric-mrr",
      type: "metric",
      title: "MRR",
      config: {
        metricKey: "mrrCash",
        format: "currency",
        icon: "chart",
        color: "blue",
      },
    },
    {
      id: "setters-metric-newcash",
      type: "metric",
      title: "New Cash (excl. MRR)",
      config: {
        metricKey: "newCashCollected",
        format: "currency",
        icon: "dollar",
        color: "brand",
      },
    },

    // --- Row 2: Commission Card (y:4) ---
    {
      id: "setters-commission",
      type: "commission",
      title: "Setter Commission (5%)",
      config: {
        cashKey: "totalCashCollected",
        rate: 0.05,
        colorTheme: "emerald",
        description: "5% of total cash collected",
      },
    },

    // --- Row 3: Performance Stats (y:8) ---
    {
      id: "setters-stat-showrate",
      type: "stat",
      title: "Show Rate",
      config: { metricKey: "showRate", format: "percent" },
    },
    {
      id: "setters-stat-closerate",
      type: "stat",
      title: "Close Rate",
      config: { metricKey: "closeRate", format: "percent" },
    },
    {
      id: "setters-stat-callsdue",
      type: "stat",
      title: "Calls Due",
      config: { metricKey: "callsDue", format: "number" },
    },
    {
      id: "setters-stat-callstaken",
      type: "stat",
      title: "Calls Taken",
      config: { metricKey: "callsTaken", format: "number" },
    },
    {
      id: "setters-stat-callsclosed",
      type: "stat",
      title: "Calls Closed",
      config: { metricKey: "callsClosed", format: "number" },
    },

    // --- Row 4: Average Stats (y:10) ---
    {
      id: "setters-stat-avgcashcall",
      type: "stat",
      title: "Avg. Cash / Call",
      config: { metricKey: "avgCashPerCall", format: "currency" },
    },
    {
      id: "setters-stat-avgcashclose",
      type: "stat",
      title: "Avg. Cash / Close",
      config: { metricKey: "avgCashPerClose", format: "currency" },
    },
    {
      id: "setters-stat-avgcashapp",
      type: "stat",
      title: "Avg. Cash / Application",
      config: { metricKey: "avgCashPerApplication", format: "currency" },
    },
    {
      id: "setters-stat-avgcashoptin",
      type: "stat",
      title: "Avg. Cash / Opt-in",
      config: { metricKey: "avgCashPerOptin", format: "currency" },
    },

    // --- Row 5: Charts (y:12) ---
    {
      id: "setters-area-showrate",
      type: "areaChart",
      title: "Show Rate Trend",
      config: {
        dataKey: "showRateTrend",
        title: "Show Rate Trend",
        yFormat: "percent",
        tooltipFormat: "percent",
      },
    },
    {
      id: "setters-pie-dealstatus",
      type: "pieChart",
      title: "Deal Status Breakdown",
      config: { dataKey: "outcomeBreakdown", title: "Deal Status Breakdown" },
    },

    // --- Row 6: Table (y:24) ---
    {
      id: "setters-table-performance",
      type: "table",
      title: "Setter Performance",
      config: { dataKey: "setterPerformance", title: "Setter Performance" },
    },
  ],

  gridLayout: [
    // Row 1: 4 metric cards
    { i: "setters-metric-cash", x: 0, y: 0, w: 3, h: 3 },
    { i: "setters-metric-revenue", x: 3, y: 0, w: 3, h: 3 },
    { i: "setters-metric-mrr", x: 6, y: 0, w: 3, h: 3 },
    { i: "setters-metric-newcash", x: 9, y: 0, w: 3, h: 3 },

    // Row 2: commission card (full-width)
    { i: "setters-commission", x: 0, y: 3, w: 12, h: 3 },

    // Row 3: 5 stat cards (2+2+3+3+2 = 12)
    { i: "setters-stat-showrate", x: 0, y: 6, w: 2, h: 2 },
    { i: "setters-stat-closerate", x: 2, y: 6, w: 2, h: 2 },
    { i: "setters-stat-callsdue", x: 4, y: 6, w: 3, h: 2 },
    { i: "setters-stat-callstaken", x: 7, y: 6, w: 3, h: 2 },
    { i: "setters-stat-callsclosed", x: 10, y: 6, w: 2, h: 2 },

    // Row 4: 4 average stat cards
    { i: "setters-stat-avgcashcall", x: 0, y: 8, w: 3, h: 2 },
    { i: "setters-stat-avgcashclose", x: 3, y: 8, w: 3, h: 2 },
    { i: "setters-stat-avgcashapp", x: 6, y: 8, w: 3, h: 2 },
    { i: "setters-stat-avgcashoptin", x: 9, y: 8, w: 3, h: 2 },

    // Row 5: area chart + pie chart side-by-side
    { i: "setters-area-showrate", x: 0, y: 10, w: 6, h: 12 },
    { i: "setters-pie-dealstatus", x: 6, y: 10, w: 6, h: 12 },

    // Row 6: table
    { i: "setters-table-performance", x: 0, y: 22, w: 12, h: 10 },
  ],
};
